import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Card, Statistic, Icon, Table } from 'antd';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    AreaChart,
    Tooltip,
    Area,
    Legend
} from 'recharts';
import moment from 'moment-timezone';

@inject('store')
@observer
export class PerformanceView extends Component {
    render() {
        const { exchangePosition, position, performance } = this.props.store.makoBotStore;

        if (!exchangePosition) return null;

        return (
            <Row gutter={16}>
                <Col xs={24} md={6}>
                    <Card className="dark" bordered={false} style={{ marginBottom: 15 }}>
                        <Statistic
                            title="Account Balance"
                            valueStyle={{ color: '#fff' }}
                            value={exchangePosition.wallet_balance}
                            precision={8}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card className="dark" bordered={false} style={{ marginBottom: 15 }}>
                        <Statistic
                            title="Open Contracts"
                            valueStyle={{
                                color: exchangePosition.side === 'Buy' ? '#29c0b1' : '#ff3366'
                            }}
                            prefix={
                                exchangePosition.side === 'Buy' ? (
                                    <Icon type="arrow-up" />
                                ) : (
                                    <Icon type="arrow-down" />
                                )
                            }
                            value={exchangePosition.size}
                            precision={0}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card className="dark" bordered={false} style={{ marginBottom: 15 }}>
                        <Statistic
                            title="Daily Realized P&L"
                            valueStyle={{
                                color: exchangePosition.realised_pnl >= 0 ? '#29c0b1' : '#ff3366'
                            }}
                            value={exchangePosition.realised_pnl}
                            suffix={`/ $${exchangePosition.realised_pnl_usd.toFixed(2)}`}
                            precision={8}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card className="dark" bordered={false} style={{ marginBottom: 15 }}>
                        <Statistic
                            title="Unrealized P&L"
                            valueStyle={{
                                color: position && position.pnl >= 0 ? '#29c0b1' : '#ff3366'
                            }}
                            value={(position && position.pnl.toFixed(8)) || 0}
                            suffix={`/ $${(position &&
                                position.pnl_usd &&
                                position.pnl_usd.toFixed(2)) ||
                                '0'}`}
                            precision={8}
                        />
                    </Card>
                </Col>
                <Col span={24}>
                    <Card
                        title="Daily Performance"
                        bordered={false}
                        size="small"
                        style={{ marginBottom: 15 }}
                        headStyle={{ backgroundColor: '#1c1e2f' }}
                        bodyStyle={{
                            backgroundColor: '#1c1e2f',
                            borderBottomLeftRadius: 6,
                            borderBottomRightRadius: 6
                        }}
                    >
                        <Table
                            style={{ marginTop: 15 }}
                            scroll={{ x: 1000 }}
                            rowKey={o => {
                                return o._id.date;
                            }}
                            dataSource={performance}
                            columns={[
                                {
                                    title: 'Date',
                                    dataIndex: 'date',
                                    key: 'date',
                                    render(text, o) {
                                        return o._id.date;
                                    }
                                },
                                {
                                    title: 'Trades',
                                    dataIndex: 'trades',
                                    key: 'trades'
                                },
                                {
                                    title: 'Longs',
                                    dataIndex: 'long_trades',
                                    key: 'long_trades'
                                },
                                {
                                    title: 'Shorts',
                                    dataIndex: 'short_trades',
                                    key: 'short_trades'
                                },
                                {
                                    title: 'Won',
                                    dataIndex: 'won',
                                    key: 'won'
                                },
                                {
                                    title: 'Lost',
                                    dataIndex: 'lost',
                                    key: 'lost'
                                },
                                {
                                    title: 'Break Even',
                                    dataIndex: 'break_even',
                                    key: 'break_even'
                                },
                                {
                                    title: 'Volume',
                                    dataIndex: 'volume',
                                    key: 'volume',
                                    render(text) {
                                        return text.toFixed(8);
                                    }
                                },
                                {
                                    title: 'P&L(Excl fee)',
                                    dataIndex: 'pnl',
                                    key: 'pnl',
                                    render(text, o) {
                                        return (
                                            <span className={text < 0 ? 'error' : 'success'}>
                                                {text.toFixed(8)} (${o.pnl_usd.toFixed(2)})
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: 'Fees',
                                    dataIndex: 'fees',
                                    key: 'fees',
                                    render(text, o) {
                                        return (
                                            <span className={text < 0 ? 'success' : 'error'}>
                                                {(text * -1).toFixed(8)} ($
                                                {(o.fees_usd * -1).toFixed(2)})
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: 'Total',
                                    dataIndex: 'total_pnl',
                                    key: 'total_pnl',
                                    render(text, o) {
                                        return (
                                            <span className={text < 0 ? 'error' : 'success'}>
                                                {text.toFixed(8)} (${o.total_pnl_usd.toFixed(2)})
                                            </span>
                                        );
                                    }
                                }
                            ]}
                        />
                    </Card>
                </Col>
                {this.renderPNLChart()}
            </Row>
        );
    }

    renderPNLChart() {
        const { pnl } = this.props.store.makoBotStore;

        return (
            <Col span={24}>
                <Card
                    title="Profit &amp; Loss"
                    bordered={false}
                    size="small"
                    style={{ marginBottom: 15 }}
                    headStyle={{ backgroundColor: '#1c1e2f' }}
                    bodyStyle={{
                        backgroundColor: '#1c1e2f',
                        borderBottomLeftRadius: 6,
                        borderBottomRightRadius: 6
                    }}
                >
                    <ResponsiveContainer height={450} width="100%">
                        <AreaChart data={pnl.slice().reverse()}>
                            <XAxis
                                dataKey="exec_time"
                                tickFormatter={ut => {
                                    return moment(ut).format('DD/MM/YY');
                                }}
                            />
                            <YAxis
                                width={100}
                                allowDataOverflow
                                domain={[dataMin => 0 - Math.abs(dataMin), dataMax => dataMax * 2]}
                            />
                            <Tooltip />
                            <Legend />
                            <Area
                                allowDecimals
                                type="monotone"
                                name="P &amp; L"
                                dataKey="amount"
                                stroke="#8884d8"
                                strokeWidth="3"
                                fillOpacity={0.3}
                                fill="#8884d8"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                <Card
                    title="Balance"
                    bordered={false}
                    size="small"
                    headStyle={{ backgroundColor: '#1c1e2f' }}
                    bodyStyle={{
                        backgroundColor: '#1c1e2f',
                        borderBottomLeftRadius: 6,
                        borderBottomRightRadius: 6
                    }}
                >
                    <ResponsiveContainer height={450} width="100%">
                        <AreaChart data={pnl.slice().reverse()}>
                            <XAxis
                                dataKey="exec_time"
                                tickFormatter={ut => {
                                    return moment(ut).format('DD/MM/YY');
                                }}
                            />
                            <YAxis
                                width={100}
                                allowDataOverflow
                                domain={[dataMin => 0 - Math.abs(dataMin), dataMax => dataMax * 2]}
                            />
                            <Tooltip />
                            <Legend />
                            <Area
                                type="monotone"
                                name="Wallet Balance"
                                dataKey="wallet_balance"
                                stroke="#8884d8"
                                fillOpacity={0.3}
                                fill="#8884d8"
                                strokeWidth="3"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            </Col>
        );
    }
}



// WEBPACK FOOTER //
// ./src/Account/MakoBot/TradeView/PerformanceView/index.js